import { useState, useCallback, useEffect, useRef } from 'react'
import { defaultSearchCategory, type ISearchCategory } from '../../../../constants/searchCategory'
import { search } from '../../../../apiClients/search/searchClient'
import type { SearchResults } from '../../../../apiClients/search/interfaces'

const SEARCH_MIN_LENGTH = 3
const DEBOUNCE_DELAY = 300 // ms

export interface UseSearchStateReturn {
  query: string
  setQuery: (value: string) => void
  selectedCategory: ISearchCategory
  setSelectedCategory: (category: ISearchCategory) => void
  searchResults: SearchResults | null
  isSearching: boolean
  searchError: Error | null
  clearSearch: () => void
}

export function useSearchState(): UseSearchStateReturn {
  const [query, setQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState(defaultSearchCategory)
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState<Error | null>(null)
  
  const debounceTimerRef = useRef<number | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const performSearch = useCallback(async (searchQuery: string, category: ISearchCategory) => {
    // Отменяем предыдущий запрос если он есть
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    setIsSearching(true)
    setSearchError(null)

    try {
      const results = await search({
        query: searchQuery,
        category: category.value,
      })
      
      // Проверяем, не был ли запрос отменен
      if (!abortController.signal.aborted) {
        setSearchResults(results)
      }
    } catch (error) {
      if (!abortController.signal.aborted) {
        setSearchError(error instanceof Error ? error : new Error('Search failed'))
        setSearchResults(null)
      }
    } finally {
      if (!abortController.signal.aborted) {
        setIsSearching(false)
      }
    }
  }, [])

  // Debounced search effect
  useEffect(() => {
    // Очищаем предыдущий таймер
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current)
    }

    // Если запрос слишком короткий, очищаем результаты
    if (query.length < SEARCH_MIN_LENGTH) {
      setSearchResults(null)
      setSearchError(null)
      setIsSearching(false)
      return
    }

    // Устанавливаем новый таймер для debounce
    debounceTimerRef.current = window.setTimeout(() => {
      performSearch(query, selectedCategory)
    }, DEBOUNCE_DELAY)

    // Cleanup function
    return () => {
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current)
      }
    }
  }, [query, selectedCategory, performSearch])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current)
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const clearSearch = useCallback(() => {
    setQuery('')
    setSearchResults(null)
    setSearchError(null)
    setIsSearching(false)
  }, [])

  return {
    query,
    setQuery,
    selectedCategory,
    setSelectedCategory,
    searchResults,
    isSearching,
    searchError,
    clearSearch,
  }
}
